import React, { useEffect, useMemo, useRef, useState } from 'react';
import Head from 'next/head';
import AuthLayout from 'components/AuthLayout';
import {
  Box,
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Avatar,
  Stack,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import Link from 'components/common/Link';
import Loader from 'components/common/Loader';
import { useSession } from 'components/SessionContext';
import moment from 'moment-timezone';
import { sendApiRequest } from 'lib/api/public';
import {
  candidateInterviewsHref,
  candidateTasksHref,
  taskHref,
} from 'lib/detailPageTabs';
import notify from 'lib/notify';
import { formatUserDateTime, getTablePreferences, setTablePreferences, stripHtml } from 'components/utils';
import { ReorderPopover } from 'components/common/utils';
import {
  mergeTwoColumnOrderWithDefaults,
  useCardLayoutReorder,
  type CardLayoutReorderHandle,
  type TwoColumnState,
} from 'hooks/useCardLayoutReorder';
import WorkIcon from '@mui/icons-material/Work';
import EventIcon from '@mui/icons-material/Event';
import TaskIcon from '@mui/icons-material/Task';
import BusinessIcon from '@mui/icons-material/Business';
import { Candidate } from 'components/candidate/types';
import { Task } from 'components/task/types';
import { Client, ClientStatus } from 'components/client/types';
import ClientStatusFilter from 'components/client/ClientStatusFilter';
import PipelineStatus from 'components/common/PipelineStatus';
import ChangelogAnnouncement from 'components/ChangelogAnnouncement';
import useInterviewsWithoutNotes from './useInterviewsWithoutNotes';

export type IEvent = {
  _id: string;
  title: string;
  startDate: string;
  startTime: string;
  startDateTime: Date;
  endDate: string;
  endTime: string;
  endDateTime: Date;
  description: string;
  participants: {
    type: string;
    _id: string;
    email: string;
    name: string;
  }[];
  candidate?: Candidate;
};

type JobStats = {
  _id: string;
  title: string;
  numberOfCandidates: number;
  daysOld: number | null;
  createdAt: Date;
  filledAt: Date | null;
};

export type GetHighlightStyle = SxProps<Theme>;

// Reusable internal components
export const StatCard = ({ title, value, onClick }: { title: string; value: number; onClick?: () => void }) => {
  return (
    <Grid size={{ xs: 6, sm: 4, md: 3 }} sx={{ flex: { lg: '1 1 0%' }, minWidth: { lg: 0 } }}>
      <Card
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        aria-label={onClick ? `${title}: ${value}` : undefined}
        onClick={onClick}
        onKeyDown={
          onClick
            ? (event: React.KeyboardEvent) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
        sx={{
          flex: 1,
          p: { xs: 1.25, sm: 2 },
          height: '100%',
          borderRadius: 2,
          borderLeft: '8px solid',
          borderLeftColor: 'primary.main',
          backgroundColor: 'background.paper',
          cursor: onClick ? 'pointer' : 'default',
          width: '100%',
          minWidth: 0,
          '&:focus-visible': onClick
            ? {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              }
            : undefined,
        }}
      >
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: { xs: '0.75rem', sm: '0.8125rem' },
          }}
        >
          {title}
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              color="text.primary"
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, lineHeight: 1.2 }}
            >
              {value}
            </Typography>
          </Box>
        </Stack>
      </Card>
    </Grid>
  );
};

export const EmptyState = ({ message }: { message: string }) => (
  <Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
    <Typography color="text.secondary" variant="body2">
      {message}
    </Typography>
  </Box>
);

export const DashboardBlock = ({
  title,
  icon,
  children,
  titleAction,
  gridSize = { xs: 12, md: 6 },
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  titleAction?: React.ReactNode;
  gridSize?: { xs: number; md: number };
}) => {
  return (
    <Grid size={{ xs: gridSize.xs, md: gridSize.md }} sx={{ minWidth: 0, maxWidth: '100%' }}>
      <Card
        elevation={1}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        <CardHeader
          avatar={<Avatar sx={{ bgcolor: 'action.hover', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>{icon}</Avatar>}
          title={
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: { xs: '0.9rem', sm: '1rem' },
              }}
            >
              {title}
            </Typography>
          }
          action={titleAction}
          sx={{
            py: { xs: 1, sm: 1.5 },
            px: { xs: 1.5, sm: 2 },
            backgroundColor: 'background.paper',
            borderBottom: '1px solid',
            borderBottomColor: 'divider',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            alignItems: 'center',
            gap: { xs: 0.5, md: 0 },
            '& .MuiCardHeader-content': {
              minWidth: 0,
              overflow: 'hidden',
            },
            '& .MuiCardHeader-action': {
              alignSelf: { xs: 'stretch', md: 'center' },
              m: 0,
              ml: { xs: 0, md: 'auto' },
              mt: { xs: 1, md: 0 },
              width: { xs: '100%', md: 'auto' },
              maxWidth: '100%',
            },
          }}
        />
        <CardContent
          sx={{
            flexGrow: 1,
            p: 0,
            '&:last-child': { pb: 0 },
            backgroundColor: 'background.paper',
            overflowY: { xs: 'visible', md: 'auto' },
            maxHeight: { xs: 'none', md: '300px' },
            minWidth: 0,
          }}
        >
          {children}
        </CardContent>
      </Card>
    </Grid>
  );
};

export const listItemSx = {
  py: 1,
  px: { xs: 2, sm: 3 },
  '&:hover': { backgroundColor: 'action.hover' },
};

// Reusable job list item component
export const JobListItem = ({
  type,
  job,
  linkPath,
}: {
  type: 'active' | 'filled';
  job: JobStats;
  linkPath: string;
}) => {
  let secondaryText = `${job.numberOfCandidates} candidate${job.numberOfCandidates !== 1 ? 's' : ''} · `;
  if (type === 'active') {
    secondaryText += `${job.daysOld !== null ? `${job.daysOld} day${job.daysOld !== 1 ? 's' : ''} old` : 'New'}`;
  } else {
    const daysToHire = job.filledAt ? moment(job.filledAt).diff(moment(job.createdAt), 'days') : null;
    secondaryText += `${daysToHire !== null ? `${daysToHire} day${daysToHire !== 1 ? 's' : ''} to hire` : 'N/A'}`;
  }

  return (
    <ListItem sx={listItemSx}>
      <ListItemText
        primary={
          <Typography variant="body1" fontWeight={500}>
            <Link href={`${linkPath}?id=${job._id}`} color="text.primary" underline="hover" target="_blank">
              {job.title}
            </Link>
          </Typography>
        }
        secondary={secondaryText}
      />
    </ListItem>
  );
};

export const ClientListItem = ({ client }: { client: Client & { statusColor?: string; jobCount?: number } }) => {
  const jobCountText =
    typeof client.jobCount === 'number' ? `${client.jobCount} job${client.jobCount !== 1 ? 's' : ''}` : null;

  return (
    <ListItem sx={listItemSx}>
      <ListItemText
        primary={
          <Typography variant="body1" fontWeight={500}>
            <Link href={`/client?id=${client._id}`} color="text.primary" underline="hover" target="_blank">
              {client.name}
            </Link>
          </Typography>
        }
        secondaryTypographyProps={{ component: 'div' }}
        secondary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <PipelineStatus
              object={{
                statusLabel: client.statusLabel,
                statusColor: client.statusColor,
              }}
            />
            {jobCountText && (
              <Typography component="span" variant="body2" color="text.secondary">
                · {jobCountText}
              </Typography>
            )}
          </Box>
        }
      />
    </ListItem>
  );
};

export const renderListItemDate = (date: Date) => {
  return `${formatUserDateTime(date)} · ${moment(date).fromNow()}`;
};

const DASHBOARD_TABLE_NAME = 'dashboard';
const DASHBOARD_CLIENTS_TABLE_NAME = 'dashboard_clients';

export type DashboardStatCardId =
  | 'upcoming-interviews'
  | 'interviews-without-notes'
  | 'tasks'
  | 'clients'
  | 'active-jobs'
  | 'filled-jobs';

const DEFAULT_CARD_COLUMNS: TwoColumnState<DashboardStatCardId> = {
  left: ['upcoming-interviews', 'interviews-without-notes', 'tasks'],
  right: ['clients', 'active-jobs', 'filled-jobs'],
};

const STAT_CARD_LABELS: Record<DashboardStatCardId, string> = {
  'upcoming-interviews': 'Upcoming Interviews',
  'interviews-without-notes': 'Incomplete Interviews',
  tasks: 'Tasks',
  clients: 'Clients',
  'active-jobs': 'Active Jobs',
  'filled-jobs': 'Filled Jobs',
};

const getActiveClientStatusFilter = (statuses: ClientStatus[]): string => {
  const activeStatus = statuses.find(status => status.label === 'Active' || status.name === 'Active');

  return activeStatus?._id || '';
};

const remapLegacyDashboardCardId = (id: string): string => (id === 'reminders' ? 'tasks' : id);

const isDashboardStatCardId = (id: string): id is DashboardStatCardId =>
  [...DEFAULT_CARD_COLUMNS.left, ...DEFAULT_CARD_COLUMNS.right].includes(id as DashboardStatCardId);

const flatOrderToTwoColumns = (order: DashboardStatCardId[]): TwoColumnState<DashboardStatCardId> => {
  const left: DashboardStatCardId[] = [];
  const right: DashboardStatCardId[] = [];

  for (const id of order) {
    if (DEFAULT_CARD_COLUMNS.left.includes(id)) {
      left.push(id);
    } else if (DEFAULT_CARD_COLUMNS.right.includes(id)) {
      right.push(id);
    }
  }

  const missingLeft = DEFAULT_CARD_COLUMNS.left.filter(id => !left.includes(id));
  const missingRight = DEFAULT_CARD_COLUMNS.right.filter(id => !right.includes(id));

  return { left: [...left, ...missingLeft], right: [...right, ...missingRight] };
};

const normalizeDashboardCardColumns = (raw: unknown): TwoColumnState<DashboardStatCardId> | undefined => {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const saved = raw as { left?: unknown; right?: unknown };

    if (Array.isArray(saved.left) && Array.isArray(saved.right)) {
      return {
        left: saved.left
          .map(id => (typeof id === 'string' ? remapLegacyDashboardCardId(id) : id))
          .filter((id): id is DashboardStatCardId => typeof id === 'string' && isDashboardStatCardId(id)),
        right: saved.right
          .map(id => (typeof id === 'string' ? remapLegacyDashboardCardId(id) : id))
          .filter((id): id is DashboardStatCardId => typeof id === 'string' && isDashboardStatCardId(id)),
      };
    }
  }

  if (Array.isArray(raw)) {
    const order = raw
      .map(id => (typeof id === 'string' ? remapLegacyDashboardCardId(id) : id))
      .filter((id): id is DashboardStatCardId => typeof id === 'string' && isDashboardStatCardId(id));

    if (order.length > 0) {
      return flatOrderToTwoColumns(order);
    }
  }

  return undefined;
};

const Dashboard = () => {
  const { user } = useSession();

  const [myJobs, setMyJobs] = useState<JobStats[]>([]);
  const [myJobsTotalCount, setMyJobsTotalCount] = useState<number>(0);
  const [myJobsLoading, setMyJobsLoading] = useState<boolean>(false);

  const [myFilledJobs, setMyFilledJobs] = useState<JobStats[]>([]);
  const [myFilledJobsTotalCount, setMyFilledJobsTotalCount] = useState<number>(0);
  const [myFilledJobsLoading, setMyFilledJobsLoading] = useState<boolean>(false);

  const [upcomingInterviews, setUpcomingInterviews] = useState<IEvent[]>([]);
  const [upcomingInterviewsTotalCount, setUpcomingInterviewsTotalCount] = useState<number>(0);
  const [upcomingInterviewsLoading, setUpcomingInterviewsLoading] = useState<boolean>(false);

  const [tasksTotalCount, setTasksTotalCount] = useState<number>(0);

  const [dashboardTasks, setDashboardTasks] = useState<Task[]>([]);
  const [tasksLoading, setTasksLoading] = useState<boolean>(false);

  const [clients, setClients] = useState<Client[]>([]);
  const [clientsTotalCount, setClientsTotalCount] = useState<number>(0);
  const [clientsLoading, setClientsLoading] = useState<boolean>(false);
  const [clientStatusFilter, setClientStatusFilter] = useState<string>('');
  const [clientsStatusFilterReady, setClientsStatusFilterReady] = useState(false);

  const [highlightedSection, setHighlightedSection] = useState<string | null>(null);
  const dashboardReorderRef = useRef<CardLayoutReorderHandle>(null);

  const {
    cardColumns,
    persistCardColumns,
    cardsReorderAnchorEl,
    closeCardsReorderPopover,
    renderCardsReorderBoard,
    stackDirection,
  } = useCardLayoutReorder<DashboardStatCardId>({
    ref: dashboardReorderRef,
    layout: 'cards',
    preferencesTableName: DASHBOARD_TABLE_NAME,
    defaultColumns: DEFAULT_CARD_COLUMNS,
    firstDroppableId: 'dashboard-left-column',
    secondDroppableId: 'dashboard-right-column',
    firstSlotLabel: 'Left column',
    secondSlotLabel: 'Right column',
    droppableType: 'DASHBOARD_CARD',
    stackDirection: 'horizontal',
    getDraggableId: id => id,
    renderLabel: sectionId => STAT_CARD_LABELS[sectionId],
    normalizeSaved: raw => {
      const fromCardColumns = normalizeDashboardCardColumns(raw);

      if (fromCardColumns) {
        return fromCardColumns;
      }

      return normalizeDashboardCardColumns(getTablePreferences(DASHBOARD_TABLE_NAME).statCardOrder);
    },
  });

  const getDashboardTasks = async () => {
    try {
      setTasksLoading(true);
      const response = await sendApiRequest<{ tasks: Task[]; totalCount: number }>({
        path: '/users/dashboard/tasks',
        method: 'GET',
      });
      setDashboardTasks(response.tasks || []);
      setTasksTotalCount(response.totalCount);
    } catch (error) {
      notify(error);
    } finally {
      setTasksLoading(false);
    }
  };

  const getMyJobs = async () => {
    try {
      setMyJobsLoading(true);
      const response = (await sendApiRequest({
        path: '/users/dashboard/my-active-jobs',
        method: 'GET',
      })) as { stats: JobStats[]; totalCount: number };
      setMyJobs(response.stats);
      setMyJobsTotalCount(response.totalCount);
    } catch (error) {
      notify(error);
    } finally {
      setMyJobsLoading(false);
    }
  };

  const getMyFilledJobs = async () => {
    try {
      setMyFilledJobsLoading(true);
      const response = (await sendApiRequest({
        path: '/users/dashboard/my-filled-jobs',
        method: 'GET',
      })) as { stats: JobStats[]; totalCount: number };
      setMyFilledJobs(response.stats);
      setMyFilledJobsTotalCount(response.totalCount);
    } catch (error) {
      notify(error);
    } finally {
      setMyFilledJobsLoading(false);
    }
  };

  const getUpcomingInterviews = async () => {
    try {
      setUpcomingInterviewsLoading(true);
      const response = (await sendApiRequest({
        path: '/users/dashboard/upcoming-interviews',
        method: 'GET',
      })) as { events: IEvent[]; totalCount: number };
      setUpcomingInterviews(response.events);
      setUpcomingInterviewsTotalCount(response.totalCount);
    } catch (error) {
      notify(error);
    } finally {
      setUpcomingInterviewsLoading(false);
    }
  };

  const getClients = async (statusFilter: string) => {
    try {
      setClientsLoading(true);
      const response = (await sendApiRequest({
        path: `clients/list?page=1&search=&sortField=createdAt&sortDirection=desc&source=dashboard${statusFilter ? `&status=${statusFilter}` : ''}`,
        method: 'GET',
      })) as { list: Client[]; totalCount: number };
      setClients(response.list);
      setClientsTotalCount(response.totalCount);
    } catch (error) {
      notify(error);
    } finally {
      setClientsLoading(false);
    }
  };

  useEffect(() => {
    void getMyJobs();
    void getMyFilledJobs();
    void getUpcomingInterviews();
    void getDashboardTasks();
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const response = (await sendApiRequest({
          path: '/clients/statuses',
          method: 'GET',
        })) as { list: ClientStatus[] };

        const statuses = (response.list || []).filter(status => status.useForProspect !== true);
        const savedFilter = getTablePreferences(DASHBOARD_CLIENTS_TABLE_NAME).statusFilter as string | undefined;
        let nextFilter = savedFilter ?? '';

        if (!savedFilter) {
          nextFilter = getActiveClientStatusFilter(statuses);

          if (nextFilter) {
            setTablePreferences({
              tableName: DASHBOARD_CLIENTS_TABLE_NAME,
              values: [{ key: 'statusFilter', value: nextFilter }],
            });
          }
        }

        setClientStatusFilter(nextFilter);
        setClientsStatusFilterReady(true);
      } catch (error) {
        notify(error);
        setClientsStatusFilterReady(true);
      }
    })();
  }, []);

  useEffect(() => {
    if (!clientsStatusFilterReady) {
      return;
    }

    void getClients(clientStatusFilter);
  }, [clientStatusFilter, clientsStatusFilterReady]);

  useEffect(() => {
    if (!user?._id) {
      return;
    }

    const preferences = getTablePreferences(DASHBOARD_TABLE_NAME);
    const raw = preferences.cardColumns ?? preferences.statCardOrder;
    const normalized = normalizeDashboardCardColumns(raw);

    persistCardColumns(mergeTwoColumnOrderWithDefaults(normalized, DEFAULT_CARD_COLUMNS));
  }, [user?._id, persistCardColumns]);

  const timezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const renderEvent = (event: IEvent, index: number) => {
    return (
      <React.Fragment key={event._id}>
        {index > 0 && <Divider sx={{ backgroundColor: 'divider' }} />}
        <ListItem sx={listItemSx}>
          <ListItemText
            primary={
              <Typography variant="body1" fontWeight={500}>
                {event.candidate ? (
                  <Link
                    href={candidateInterviewsHref(event.candidate._id)}
                    color="text.primary"
                    underline="hover"
                    target="_blank"
                  >
                    {event.title}
                  </Link>
                ) : (
                  event.title
                )}
              </Typography>
            }
            secondary={renderListItemDate(event.startDateTime)}
          />
        </ListItem>
      </React.Fragment>
    );
  };

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
      setHighlightedSection(sectionId);
      setTimeout(() => {
        setHighlightedSection(null);
      }, 2000);
    }
  };

  // Style for highlighted sections (no layout shift: fixed border width, no padding change)
  const getHighlightStyle = (sectionId: string): GetHighlightStyle => {
    const active = highlightedSection === sectionId;
    return (theme: Theme) => ({
      transition: 'background-color 0.4s ease, border-color 0.4s ease',
      backgroundColor: active ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
      borderLeft: '3px solid',
      borderLeftColor: active ? theme.palette.primary.main : 'transparent',
    });
  };

  const { mainContent: interviewsWithoutNotesMainContent, statCard: interviewsWithoutNotesStatCard } =
    useInterviewsWithoutNotes({
      getHighlightStyle,
      scrollToSection,
    });

  const statCardsById = useMemo(
    () => ({
      'upcoming-interviews': (
        <StatCard
          title="Upcoming Interviews"
          value={upcomingInterviewsTotalCount}
          onClick={() => scrollToSection('upcoming-interviews')}
        />
      ),
      'interviews-without-notes': interviewsWithoutNotesStatCard,
      tasks: <StatCard title="Tasks" value={tasksTotalCount} onClick={() => scrollToSection('tasks')} />,
      clients: <StatCard title="Clients" value={clientsTotalCount} onClick={() => scrollToSection('clients')} />,
      'active-jobs': (
        <StatCard title="Active Jobs" value={myJobsTotalCount} onClick={() => scrollToSection('active-jobs')} />
      ),
      'filled-jobs': (
        <StatCard title="Filled Jobs" value={myFilledJobsTotalCount} onClick={() => scrollToSection('filled-jobs')} />
      ),
    }),
    [
      upcomingInterviewsTotalCount,
      interviewsWithoutNotesStatCard,
      tasksTotalCount,
      clientsTotalCount,
      myJobsTotalCount,
      myFilledJobsTotalCount,
    ],
  );
  const orderedStatCardIds = useMemo(() => [...cardColumns.left, ...cardColumns.right], [cardColumns]);
  const columnWidthSx = { width: { xs: '100%', lg: 'calc(50% - 8px)' }, minWidth: 0, maxWidth: '100%' };

  const renderDashboardCard = (id: DashboardStatCardId) => (
    <Box key={id} sx={{ mb: 2 }}>
      {renderDashboardBlock(id)}
    </Box>
  );

  const renderDashboardColumn = (items: DashboardStatCardId[]) => (
    <Box sx={columnWidthSx}>{items.map(renderDashboardCard)}</Box>
  );

  const renderDashboardBlock = (id: DashboardStatCardId) => {
    switch (id) {
      case 'upcoming-interviews':
        return (
          <DashboardBlock
            title={`Upcoming Interviews (${upcomingInterviewsTotalCount})`}
            icon={<EventIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
            gridSize={{ xs: 12, md: 12 }}
          >
            <Box id="upcoming-interviews" sx={getHighlightStyle('upcoming-interviews')}>
              {upcomingInterviewsLoading ? (
                <Loader />
              ) : upcomingInterviews.length > 0 ? (
                <List disablePadding dense>
                  {upcomingInterviews.map((interview, index) => renderEvent(interview, index))}
                </List>
              ) : (
                <EmptyState message="No upcoming interviews" />
              )}
            </Box>
          </DashboardBlock>
        );
      case 'interviews-without-notes':
        return interviewsWithoutNotesMainContent;
      case 'tasks':
        return (
          <DashboardBlock
            title={`Tasks (${tasksTotalCount})`}
            icon={<TaskIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
            gridSize={{ xs: 12, md: 12 }}
          >
            <Box id="tasks" sx={getHighlightStyle('tasks')}>
              {tasksLoading ? (
                <Loader />
              ) : dashboardTasks.length > 0 ? (
                <List disablePadding dense>
                  {dashboardTasks.map((task, index) => {
                    return (
                      <React.Fragment key={task._id}>
                        {index > 0 && <Divider sx={{ backgroundColor: 'divider' }} />}
                        <ListItem sx={listItemSx}>
                          <ListItemText
                            primary={
                              <Typography variant="body1" fontWeight={500}>
                                <Link href={taskHref(task._id)} target="_blank" color="text.primary" underline="hover">
                                  {task.title || 'Untitled task'}
                                </Link>
                              </Typography>
                            }
                            secondaryTypographyProps={{ component: 'div' }}
                            secondary={
                              <>
                                {task.dueDate
                                  ? renderListItemDate(moment(task.dueDate).tz(timezone).toDate())
                                  : null}
                                {task.candidateId && task.candidateName ? (
                                  <Box sx={{ mt: 0.5 }}>
                                    <Link href={candidateTasksHref(task.candidateId)} target="_blank" underline="hover">
                                      {task.candidateName}
                                    </Link>
                                  </Box>
                                ) : null}
                                {task.description ? (
                                  <Box sx={{ mt: 1, fontStyle: 'italic' }}>
                                    &quot;{stripHtml(task.description)}&quot;
                                  </Box>
                                ) : null}
                              </>
                            }
                          />
                        </ListItem>
                      </React.Fragment>
                    );
                  })}
                </List>
              ) : (
                <EmptyState message="No open tasks" />
              )}
            </Box>
          </DashboardBlock>
        );
      case 'clients':
        return (
          <DashboardBlock
            title={`Clients (${clientsTotalCount})`}
            icon={<BusinessIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
            gridSize={{ xs: 12, md: 12 }}
            titleAction={
              clientsStatusFilterReady ? (
                <ClientStatusFilter
                  tableName={DASHBOARD_CLIENTS_TABLE_NAME}
                  layout="combobox"
                  sx={{ width: { xs: '100%', md: 250 }, maxWidth: '100%' }}
                  onChange={value => {
                    setClientStatusFilter(value);
                  }}
                />
              ) : null
            }
          >
            <Box id="clients" sx={getHighlightStyle('clients')}>
              {clientsLoading ? (
                <Loader />
              ) : clients.length > 0 ? (
                <>
                  <List disablePadding dense>
                    {clients.map((client, index) => (
                      <React.Fragment key={client._id}>
                        {index > 0 && <Divider sx={{ backgroundColor: 'divider' }} />}
                        <ClientListItem client={client} />
                      </React.Fragment>
                    ))}
                  </List>
                  {clients.length < clientsTotalCount && (
                    <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid', borderTopColor: 'divider' }}>
                      <Link href="/clients" color="secondary" underline="hover" target="_blank">
                        View All ({clientsTotalCount}) →
                      </Link>
                    </Box>
                  )}
                </>
              ) : (
                <EmptyState message="No clients" />
              )}
            </Box>
          </DashboardBlock>
        );
      case 'active-jobs':
        return (
          <DashboardBlock
            title={`Active Jobs (${myJobsTotalCount})`}
            icon={<WorkIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
            gridSize={{ xs: 12, md: 12 }}
          >
            <Box id="active-jobs" sx={getHighlightStyle('active-jobs')}>
              {myJobsLoading ? (
                <Loader />
              ) : myJobs.length > 0 ? (
                <>
                  <List disablePadding dense>
                    {myJobs.map((job, index) => (
                      <React.Fragment key={job._id}>
                        {index > 0 && <Divider sx={{ backgroundColor: 'divider' }} />}
                        <JobListItem type="active" job={job} linkPath="/job" />
                      </React.Fragment>
                    ))}
                  </List>
                  {myJobs.length < myJobsTotalCount && (
                    <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid', borderTopColor: 'divider' }}>
                      <Link href="/jobs?isActive=true" color="secondary" underline="hover" target="_blank">
                        View All ({myJobsTotalCount}) →
                      </Link>
                    </Box>
                  )}
                </>
              ) : (
                <EmptyState message="No active jobs found" />
              )}
            </Box>
          </DashboardBlock>
        );
      case 'filled-jobs':
        return (
          <DashboardBlock
            title={`Filled Jobs (${myFilledJobsTotalCount})`}
            icon={<TaskIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
            gridSize={{ xs: 12, md: 12 }}
          >
            <Box id="filled-jobs" sx={getHighlightStyle('filled-jobs')}>
              {myFilledJobsLoading ? (
                <Loader />
              ) : myFilledJobs.length > 0 ? (
                <>
                  <List disablePadding dense>
                    {myFilledJobs.map((job, index) => (
                      <React.Fragment key={job._id}>
                        {index > 0 && <Divider sx={{ backgroundColor: 'divider' }} />}
                        <JobListItem type="filled" job={job} linkPath="/job" />
                      </React.Fragment>
                    ))}
                  </List>
                  {myFilledJobs.length < myFilledJobsTotalCount && (
                    <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid', borderTopColor: 'divider' }}>
                      <Link href="/jobs?isFilled=true" color="secondary" underline="hover" target="_blank">
                        View All ({myFilledJobsTotalCount}) →
                      </Link>
                    </Box>
                  )}
                </>
              ) : (
                <EmptyState message="No filled jobs" />
              )}
            </Box>
          </DashboardBlock>
        );
      default:
        return null;
    }
  };

  return (
    <AuthLayout breadCrumb={{ title: 'Overview', titleText: 'Overview' }}>
      <Head>
        <title>Overview</title>
        <meta name="description" content="Overview" />
      </Head>

      <Box
        sx={{
          pb: 3,
          px: 0,
          pt: 0,
          display: 'flex',
          flexDirection: 'column',
          height: { xs: 'auto', md: 'calc(100dvh - 76px)' },
          maxHeight: { xs: 'none', md: 'calc(100dvh - 76px)' },
          overflow: { xs: 'visible', md: 'hidden' },
        }}
      >
        <ChangelogAnnouncement />

        <Box sx={{ textAlign: 'right', mb: 2, mr: 0, flexShrink: 0 }}>
          <Button
            variant="contained"
            color="inherit"
            size="small"
            onClick={event => {
              dashboardReorderRef.current?.toggleCardsReorderPopover(event);
            }}
          >
            Options
          </Button>
          <ReorderPopover
            open={Boolean(cardsReorderAnchorEl)}
            anchorEl={cardsReorderAnchorEl}
            onClose={closeCardsReorderPopover}
          >
            {renderCardsReorderBoard()}
          </ReorderPopover>
        </Box>

        <Grid
          container
          spacing={{ xs: 1.5, sm: 2 }}
          sx={{ mb: 3, mr: 0, flexShrink: 0, flexWrap: { xs: 'wrap', md: 'wrap', lg: 'nowrap' } }}
          alignItems="stretch"
        >
          {orderedStatCardIds.map(id => (
            <React.Fragment key={id}>{statCardsById[id]}</React.Fragment>
          ))}
        </Grid>

        <Box
          sx={{
            flex: { xs: 'none', md: 1 },
            minHeight: { xs: 'auto', md: 0 },
            minWidth: 0,
            overflowY: { xs: 'visible', md: 'auto' },
            mr: { xs: 0 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: stackDirection === 'vertical' ? 'column' : undefined,
              flexWrap: stackDirection === 'horizontal' ? 'wrap' : undefined,
              gap: { xs: 1.5, sm: 2 },
              width: '100%',
              minWidth: 0,
            }}
          >
            {renderDashboardColumn(cardColumns.left)}
            {renderDashboardColumn(cardColumns.right)}
          </Box>
        </Box>
      </Box>
    </AuthLayout>
  );
};

export default Dashboard;
